import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, Printer, FileText, Calendar, Stethoscope, MapPin, Anchor, ClipboardCheck, Share2, Bandage, Sparkles } from "lucide-react";
import { api, fmtDate, fmtNum, GROUP_COLORS } from "../lib/api";
import { BASELINE_SCHEMA } from "../lib/baselineSchema";
import useEscClose from "../hooks/useEscClose";
import ShareModal from "./ShareModal";
import ExplainModal from "./ExplainModal";
import {
  SEVERITY_LABEL,
  STATUS_LABEL as INJURY_STATUS_LABEL,
  typeLabel as injuryTypeLabel,
  bodyPartLabel,
} from "../lib/injuryLabels";

const STATUS_LABEL = { ok: "ok", low: "LOW", high: "HIGH", na: "" };

/**
 * Full-screen modal rendering the sailor's medical history as a print-ready
 * report. The "Print" button invokes window.print() — the print stylesheet
 * (#print-root + @media print rules in index.css) ensures only this report
 * is included in the printout, so the user can save it as PDF directly.
 */
export default function MedicalHistoryReport({ sailorId, parameters, onClose, initialMode = "full" }) {
  const [detail, setDetail] = useState(null);
  const [injuries, setInjuries] = useState([]);
  // Two print modes: "full" = entire history, "latest" = just latest test/baseline.
  const [mode, setMode] = useState(initialMode);
  const [showShare, setShowShare] = useState(false);
  // Explain modal lives at this level so the LLM panel renders outside the
  // printable area and never appears in the PDF.
  const [explainCtx, setExplainCtx] = useState(null); // { record, deviations }
  const restoreTimerRef = useRef(null);
  const prevTitleRef = useRef(null);

  useEscClose(!!sailorId && !showShare, onClose);

  useEffect(() => {
    if (!sailorId) return;
    Promise.all([
      api.get(`/sailors/${sailorId}`),
      api.get(`/injuries?sailor_id=${sailorId}`),
    ]).then(([s, inj]) => {
      setDetail(s.data);
      setInjuries(inj.data || []);
    });
  }, [sailorId]);

  // Group params by category for the per-record grid
  const paramByGroup = useMemo(() => {
    const map = {};
    (parameters || []).forEach((p) => {
      if (!map[p.group]) map[p.group] = [];
      map[p.group].push(p);
    });
    return map;
  }, [parameters]);

  const paramByKey = useMemo(() => {
    return Object.fromEntries((parameters || []).map((p) => [p.key, p]));
  }, [parameters]);

  if (!sailorId) return null;

  const doPrint = () => {
    // If a previous print's safety timer is still pending, cancel it so its
    // delayed restore doesn't stomp on this run's title.
    if (restoreTimerRef.current) {
      clearTimeout(restoreTimerRef.current);
      restoreTimerRef.current = null;
    }

    // Set the suggested filename: "<Sailor Name> Medical History <DD MMM YYYY>"
    if (prevTitleRef.current === null) {
      prevTitleRef.current = document.title;
    }
    const safeName = (detail?.name || "Athlete").replace(/[/\\?%*:|"<>]/g, "").trim();
    const printedOn = new Date().toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    }).replace(/,/g, "");
    const suffix =
      mode === "issues" ? "Lab Deviations"
      : mode === "latest" ? "Latest Medical Record"
      : "Medical History";
    document.title = `${safeName} ${suffix} ${printedOn}`;
    document.body.classList.add("printing-report");

    const restore = () => {
      if (prevTitleRef.current !== null) {
        document.title = prevTitleRef.current;
        prevTitleRef.current = null;
      }
      document.body.classList.remove("printing-report");
      if (restoreTimerRef.current) {
        clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = null;
      }
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    // safety net in case afterprint never fires (some browsers / headless)
    restoreTimerRef.current = setTimeout(restore, 4000);

    // Give Windows Chrome/Edge a tick to apply the `printing-report` body
    // class before the print dialog snapshots the document. Without this
    // delay the dialog occasionally prints the dark modal background
    // instead of the clean report.
    setTimeout(() => window.print(), 120);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-[#040D1A]/95 backdrop-blur-sm overflow-y-auto" data-testid="history-report-modal" id="history-report-portal-content">
      {/* sticky toolbar — hidden in print */}
      <div className="sticky top-0 z-10 bg-[#040D1A]/80 backdrop-blur-xl border-b border-[#1E304B]" data-noprint-toolbar>
        <div className="max-w-[1100px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-slate-400">
            Medical history report {detail ? `· ${detail.name}` : ""}
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-1 mr-1 p-0.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg" data-testid="report-mode-switch">
              <button
                onClick={() => setMode("full")}
                className={`px-2.5 py-1 rounded-md text-[11px] uppercase tracking-wider transition ${
                  mode === "full"
                    ? "bg-cyan-500/20 text-cyan-200"
                    : "text-slate-400 hover:text-slate-200"
                }`}
                data-testid="report-mode-full"
              >
                Full history
              </button>
              <button
                onClick={() => setMode("latest")}
                className={`px-2.5 py-1 rounded-md text-[11px] uppercase tracking-wider transition ${
                  mode === "latest"
                    ? "bg-cyan-500/20 text-cyan-200"
                    : "text-slate-400 hover:text-slate-200"
                }`}
                data-testid="report-mode-latest"
              >
                Latest only
              </button>
              <button
                onClick={() => setMode("issues")}
                className={`px-2.5 py-1 rounded-md text-[11px] uppercase tracking-wider transition ${
                  mode === "issues"
                    ? "bg-rose-500/20 text-rose-200"
                    : "text-slate-400 hover:text-slate-200"
                }`}
                data-testid="report-mode-issues"
                title="Print only the out-of-range parameters from the most recent lab"
              >
                Issues only
              </button>
            </div>
            <button
              onClick={doPrint}
              disabled={!detail}
              className="pill active disabled:opacity-50"
              data-testid="history-print-btn"
            >
              <Printer size={14} /> Print / Save as PDF
            </button>
            <button
              onClick={() => setShowShare(true)}
              disabled={!detail}
              className="pill disabled:opacity-50"
              data-testid="history-share-btn"
              title="Share this report via WhatsApp or Email"
            >
              <Share2 size={14} /> Share
            </button>
            <button onClick={onClose} className="pill" data-testid="history-close-btn">
              <X size={14} /> Close
            </button>
          </div>
        </div>
      </div>

      {/* PRINT AREA */}
      <div id="print-root" className="max-w-[1100px] mx-auto p-6 md:p-10">
        {!detail ? (
          <div className="text-center text-slate-500 py-20">Loading...</div>
        ) : (
          <Report
            detail={detail}
            injuries={injuries}
            paramByGroup={paramByGroup}
            paramByKey={paramByKey}
            mode={mode}
            onExplain={(record, deviations) => setExplainCtx({ record, deviations })}
          />
        )}
      </div>

      <ShareModal
        open={showShare}
        onClose={() => setShowShare(false)}
        sailorId={sailorId}
        sailorName={detail?.name}
        baseline={detail?.baseline}
        reportType={mode}
        subject={
          detail
            ? `${detail.name} — ${
                mode === "issues" ? "Lab Deviations" : mode === "latest" ? "Latest Medical Record" : "Medical History"
              }`
            : ""
        }
        bodyTemplate={
          detail
            ? `Hi {dr},\n\nSharing ${detail.name}'s ${
                mode === "issues" ? "lab deviation summary" : mode === "latest" ? "latest medical record" : "full medical history"
              } from the Yacht Club of Hyderabad. The full report opens at the link below — no login needed, valid for 7 days.\n\nPlease let us know if you need anything more.\n\nThanks.`
            : ""
        }
        contextLabel={detail ? `${detail.name} — ${mode === "issues" ? "Issues only" : mode === "latest" ? "Latest only" : "Full history"}` : ""}
      />

      <ExplainModal
        open={!!explainCtx}
        onClose={() => setExplainCtx(null)}
        sailorId={sailorId}
        sailorName={detail?.name}
        recordId={explainCtx?.record?.id || null}
        date={explainCtx?.record?.date}
        deviations={explainCtx?.deviations || []}
        contextLabel={
          explainCtx
            ? `${fmtDate(explainCtx.record.date)} · ${explainCtx.deviations.length} marker${explainCtx.deviations.length === 1 ? "" : "s"} out of range`
            : ""
        }
      />
    </div>,
    document.body
  );
}

function Report({ detail, injuries = [], paramByGroup, paramByKey, mode = "full", onExplain }) {
  // Memoised newest-first sort — declared before the early return below so
  // hooks are called in the same order on every render (Rules of Hooks).
  const sortedInjuries = useMemo(
    () => [...(injuries || [])].sort((a, b) => (b.onset_date || "").localeCompare(a.onset_date || "")),
    [injuries],
  );

  // Short-circuit into the dedicated "Issues only" report — keeps the layout
  // tight and prevents any of the full-history machinery from running.
  if (mode === "issues") {
    return <IssuesReport detail={detail} paramByKey={paramByKey} onExplain={onExplain} />;
  }
  const isLatestOnly = mode === "latest";
  // Lab records (bloodwork + consultations) — biometrics live in their own
  // collection of records and shouldn't pollute test counts or the history.
  const labRecords = (detail.records || []).filter((r) => r.type !== "biometric");
  const biometricRecords = (detail.records || []).filter((r) => r.type === "biometric");

  const latest = labRecords[labRecords.length - 1];
  const first = labRecords[0];
  const today = new Date().toISOString().slice(0, 10);

  // For "latest only" mode we usually want the latest record that ACTUALLY
  // contains bloodwork results (params). When the most recent visit was just
  // a consultation (advice with no labs), fall back further so the report
  // still shows real numbers.
  const latestLab = [...labRecords].reverse().find(
    (r) => r.params && Object.keys(r.params).length > 0,
  );
  const latestConsult = [...labRecords].reverse().find(
    (r) => r.dr || r.hospital || r.advice,
  );

  // Most recent height & weight from the biometric sub-collection.
  const sortedBio = [...biometricRecords].sort((a, b) => a.date.localeCompare(b.date));
  let lastHeight = null, lastHeightDate = null;
  let lastWeight = null, lastWeightDate = null;
  sortedBio.forEach((r) => {
    const h = r.params?.height;
    const w = r.params?.weight;
    if (h != null) { lastHeight = h; lastHeightDate = r.date; }
    if (w != null) { lastWeight = w; lastWeightDate = r.date; }
  });
  const bmi =
    lastHeight && lastWeight ? Math.round((lastWeight / ((lastHeight / 100) ** 2)) * 10) / 10 : null;
  const bmiZone =
    bmi == null ? null
      : bmi < 18.5 ? { label: "Underweight", color: "#F59E0B" }
      : bmi < 25 ? { label: "Normal", color: "#10B981" }
      : bmi < 30 ? { label: "Overweight", color: "#F59E0B" }
      : { label: "Obese", color: "#EF4444" };

  // Collect deficiency counts across full history (lab records only).
  const issueCounts = {};
  labRecords.forEach((rec) => {
    for (const [k, st] of Object.entries(rec.status || {})) {
      if (st === "low" || st === "high") issueCounts[k] = (issueCounts[k] || 0) + 1;
    }
  });
  const topIssues = Object.entries(issueCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  // Records that have any text content (dr / hospital / advice).
  const prescriptions = labRecords.filter((r) => r.dr || r.hospital || r.advice);

  return (
    <div className="report-doc">
      {/* HEADER */}
      <header className="border-b-2 border-cyan-400/60 pb-5 mb-7">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="/branding/favicon-192.png"
              alt="YCH"
              className="w-14 h-14 rounded-lg bg-white p-1 object-contain border border-cyan-500/40 print:bg-white"
            />
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-300/80">
                Yacht Club of Hyderabad — {isLatestOnly ? "Latest Medical Record" : "Medical Record"}
              </div>
              <div className="font-display font-extrabold text-3xl mt-0.5 text-slate-100">
                {detail.name}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                <span
                  className="px-2 py-0.5 rounded-full"
                  style={{
                    background: `${GROUP_COLORS[detail.group] || "#94A3B8"}22`,
                    color: GROUP_COLORS[detail.group] || "#94A3B8",
                    border: `1px solid ${GROUP_COLORS[detail.group] || "#94A3B8"}66`,
                  }}
                >
                  {detail.group}
                </span>
                {detail.subgroup && (
                  <span className="px-2 py-0.5 rounded-full border border-cyan-500/40 text-cyan-200 bg-cyan-500/10 font-mono-data">
                    {detail.subgroup}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-400 font-mono-data">
            <div>Report generated</div>
            <div className="text-slate-200 font-display text-base">{fmtDate(today)}</div>
            {!isLatestOnly && (
              <>
                <div className="mt-2">Period</div>
                <div className="text-slate-200">{fmtDate(first?.date)} → {fmtDate(latest?.date)}</div>
              </>
            )}
            {isLatestOnly && latest && (
              <>
                <div className="mt-2">Latest test</div>
                <div className="text-slate-200">{fmtDate(latest.date)}</div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* SUMMARY STATS */}
      <section className="grid grid-cols-4 gap-3 mb-7">
        {isLatestOnly ? (
          <>
            <StatCard label="Latest test" value={latest ? fmtDate(latest.date) : "—"} small />
            <StatCard label="Latest weight" value={lastWeight != null ? `${fmtNum(lastWeight, 1)} kg` : "—"} small />
            <StatCard label="Latest height" value={lastHeight != null ? `${fmtNum(lastHeight, 1)} cm` : "—"} small />
            <StatCard
              label="Open flags"
              value={countIssues(latest)}
              accent={countIssues(latest) > 0 ? "#F59E0B" : "#10B981"}
            />
          </>
        ) : (
          <>
            <StatCard label="Total tests" value={labRecords.length} />
            <StatCard label="Period" value={`${yearsBetween(first?.date, latest?.date)} yrs`} />
            <StatCard label="Latest test" value={fmtDate(latest?.date)} small />
            <StatCard
              label="Open flags (latest)"
              value={countIssues(latest)}
              accent={countIssues(latest) > 0 ? "#F59E0B" : "#10B981"}
            />
          </>
        )}
      </section>

      {/* BIOMETRICS — latest known measurements only, regardless of mode */}
      {(lastHeight != null || lastWeight != null) && (
        <section className="mb-7 page-block" data-testid="report-biometrics">
          <SectionTitle>Latest biometrics</SectionTitle>
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-slate-700 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300/80 mb-1">Weight</div>
              <div className="font-mono-data text-xl text-slate-100">
                {lastWeight != null ? `${fmtNum(lastWeight, 1)} kg` : "—"}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {lastWeightDate ? `measured ${fmtDate(lastWeightDate)}` : "no measurement"}
              </div>
            </div>
            <div className="border border-slate-700 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300/80 mb-1">Height</div>
              <div className="font-mono-data text-xl text-slate-100">
                {lastHeight != null ? `${fmtNum(lastHeight, 1)} cm` : "—"}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {lastHeightDate ? `measured ${fmtDate(lastHeightDate)}` : "no measurement"}
              </div>
            </div>
            <div
              className="border rounded-lg p-3"
              style={{
                borderColor: bmi != null ? `${bmiZone.color}66` : "rgba(100,116,139,0.4)",
                background: bmi != null ? `${bmiZone.color}14` : undefined,
              }}
            >
              <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: bmi != null ? bmiZone.color : "#94A3B8" }}>
                BMI
              </div>
              <div className="font-mono-data text-xl text-slate-100">
                {bmi != null ? `${fmtNum(bmi, 1)} kg/m²` : "—"}
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: bmi != null ? bmiZone.color : "#64748B" }}>
                {bmi != null ? bmiZone.label : "needs both H + W"}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* BASELINE ASSESSMENT */}
      {detail.baseline && Object.keys(detail.baseline).length > 0 && (
        <BaselineSection baseline={detail.baseline} />
      )}

      {/* LATEST SNAPSHOT */}
      {latest && (
        <section className="mb-8 page-block">
          <SectionTitle>Latest test snapshot — {fmtDate(latest.date)}</SectionTitle>
          {(latest.hospital || latest.dr) && (
            <div className="text-xs text-slate-400 mb-3 flex flex-wrap gap-4">
              {latest.hospital && <span className="flex items-center gap-1.5"><MapPin size={12} />{latest.hospital}</span>}
              {latest.dr && <span className="flex items-center gap-1.5"><Stethoscope size={12} />{latest.dr}</span>}
            </div>
          )}
          {Object.entries(paramByGroup).map(([group, params]) => {
            const measured = params.filter((p) => latest.params?.[p.key] != null);
            if (measured.length === 0) return null;
            return (
              <div key={group} className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-cyan-300/70 mb-1">{group}</div>
                <div className="grid grid-cols-4 gap-2">
                  {measured.map((p) => {
                    const v = latest.params?.[p.key];
                    const st = latest.status?.[p.key] || "na";
                    return <ValuePill key={p.key} label={p.label} value={v} unit={p.unit} status={st} />;
                  })}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* CHRONIC ISSUES — full history view only */}
      {!isLatestOnly && topIssues.length > 0 && (
        <section className="mb-8 page-block">
          <SectionTitle>Recurring out-of-range parameters</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {topIssues.map(([k, n]) => {
              const p = paramByKey[k];
              if (!p) return null;
              return (
                <div key={k} className="border border-slate-700 rounded px-3 py-2 text-xs flex items-center justify-between">
                  <span className="text-slate-200">{p.label}</span>
                  <span className="font-mono-data text-rose-300">{n}× flagged</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* PRESCRIPTIONS / DOCTOR NOTES — full history view only */}
      {!isLatestOnly && prescriptions.length > 0 && (
        <section className="mb-8 page-block">
          <SectionTitle>Doctor visits, prescriptions & advice</SectionTitle>
          <div className="space-y-2">
            {[...prescriptions].reverse().map((r) => (
              <div key={r.id} className="border-l-2 border-cyan-500/60 pl-3 py-1.5 text-sm">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 mb-0.5">
                  <span className="font-mono-data text-slate-300 text-xs">{fmtDate(r.date)}</span>
                  {r.dr && (
                    <span className="text-slate-100 font-medium flex items-center gap-1">
                      <Stethoscope size={12} className="text-cyan-300" /> {r.dr}
                    </span>
                  )}
                  {r.hospital && (
                    <span className="text-slate-400 text-xs flex items-center gap-1">
                      <MapPin size={11} /> {r.hospital}
                    </span>
                  )}
                </div>
                {r.advice && (
                  <div className="text-slate-300 text-[13px] whitespace-pre-wrap leading-relaxed">
                    {r.advice}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* INJURIES & FIRST-AID — full history view only */}
      {!isLatestOnly && injuries.length > 0 && (
        <section className="mb-8 page-block">
          <SectionTitle>
            <Bandage size={14} className="inline mr-1.5 text-rose-300" />
            Injuries &amp; first-aid log ({injuries.length} entries)
          </SectionTitle>
          <div className="space-y-2">
            {sortedInjuries.map((inj) => {
                const isResolved = inj.status === "resolved";
                const typeStr =
                  inj.type === "other" && inj.type_other ? inj.type_other : injuryTypeLabel(inj.type);
                const bp =
                  inj.body_part === "general" && inj.body_part_other
                    ? inj.body_part_other
                    : bodyPartLabel(inj.body_part);
                return (
                  <div
                    key={inj.id}
                    className={`border-l-2 pl-3 py-1.5 text-sm ${isResolved ? "border-emerald-500/40 opacity-90" : "border-rose-500/60"}`}
                  >
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 mb-0.5">
                      <span className="font-mono-data text-slate-300 text-xs">
                        {fmtDate(inj.onset_date)}
                      </span>
                      <span className="text-slate-100 font-medium">
                        {typeStr} — {bp}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono-data">
                        {SEVERITY_LABEL[inj.severity] || inj.severity}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider font-mono-data text-slate-500">
                        · {INJURY_STATUS_LABEL[inj.status] || inj.status}
                        {isResolved && inj.resolved_date ? ` ${fmtDate(inj.resolved_date)}` : ""}
                      </span>
                      {inj.rest_required && !isResolved && (
                        <span className="text-[10px] uppercase tracking-wider font-mono-data text-amber-400">
                          · Rest{inj.days_off_training ? ` ${inj.days_off_training}d` : ""}
                        </span>
                      )}
                    </div>
                    {inj.description && (
                      <div className="text-slate-300 text-[13px] whitespace-pre-wrap leading-relaxed">
                        {inj.description}
                      </div>
                    )}
                    {inj.treatment && (
                      <div className="text-slate-400 text-[12px] mt-0.5 whitespace-pre-wrap leading-relaxed">
                        <span className="text-cyan-300 font-semibold text-[10px] uppercase mr-1.5">Treatment:</span>
                        {inj.treatment}
                      </div>
                    )}
                    <IncidentDetailsBlock inj={inj} />
                  </div>
                );
              })}
          </div>
        </section>
      )}

      {/* FULL CHRONOLOGICAL HISTORY (latest first) — skipped in latest-only mode */}
      {!isLatestOnly && (
        <section className="page-block">
          <SectionTitle>
            <FileText size={14} className="inline mr-1.5" />
            Complete history — latest first ({labRecords.length} entries)
          </SectionTitle>
          <div className="space-y-4">
            {[...labRecords].reverse().map((r) => {
            const measured = (parameters_keys_in(r, paramByKey)).filter((p) => r.params?.[p.key] != null);
            return (
              <article key={r.id} className="break-inside-avoid border border-slate-700/70 rounded-lg p-3">
                <header className="flex flex-wrap items-baseline justify-between gap-2 mb-2 border-b border-slate-800 pb-2">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="font-display text-slate-100 font-semibold text-sm flex items-center gap-1.5">
                      <Calendar size={12} className="text-cyan-300" />
                      {fmtDate(r.date)}
                    </span>
                    {r.dr && <span className="text-xs text-slate-300">Dr. {stripDrPrefix(r.dr)}</span>}
                    {r.hospital && <span className="text-xs text-slate-500">{r.hospital}</span>}
                  </div>
                  <span className="text-[10px] text-slate-500 font-mono-data">{measured.length} measurements</span>
                </header>

                {measured.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 mb-2">
                    {measured.map((p) => {
                      const v = r.params?.[p.key];
                      const st = r.status?.[p.key] || "na";
                      return (
                        <div key={p.key} className="text-[11px] flex items-baseline justify-between border border-slate-800 rounded px-2 py-1">
                          <span className="text-slate-400 truncate">{p.label}</span>
                          <span className={`font-mono-data ml-2 ${cellColor(st)}`}>
                            {fmtNum(v, 2)}
                            {STATUS_LABEL[st] && <sup className="ml-0.5 text-[8px]">{STATUS_LABEL[st]}</sup>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {r.advice && (
                  <div className="text-[12px] text-slate-300 bg-slate-800/30 border-l-2 border-cyan-500/50 pl-2 py-1 rounded whitespace-pre-wrap">
                    <span className="text-cyan-300 font-semibold text-[10px] uppercase mr-1.5">Advice:</span>
                    {r.advice}
                  </div>
                )}
              </article>
            );
          })}
          </div>
        </section>
      )}

      <footer className="mt-10 pt-4 border-t border-slate-800 text-[10px] text-slate-500 flex justify-between">
        <span>Yacht Club of Hyderabad — confidential medical record</span>
        <span className="font-mono-data">Reference ranges per Tata 1mg lab; status flags are advisory.</span>
      </footer>
    </div>
  );
}

// --- helpers ---

// "Issues only" report mode: shows only out-of-range parameters from the most
// recent lab visit that actually had measurements. Designed to fit on a single
// A4 sheet so a coach can hand it to a doctor without context overload.
function IssuesReport({ detail, paramByKey, onExplain }) {
  // Bloodwork records only — biometrics never go through low/high status logic.
  const labRecords = (detail.records || []).filter((r) => r.type !== "biometric");

  // Use the most recent record that actually contains lab numbers. A
  // consultation-only visit (advice, no labs) wouldn't tell us anything about
  // deviations, so we walk back until we find real measurements.
  const latestLab = [...labRecords].reverse().find(
    (r) => r.params && Object.keys(r.params).length > 0,
  );

  const today = new Date().toISOString().slice(0, 10);

  // Build the deviation list from the latest lab's status map. Sort so HIGH
  // values come first (typically more urgent for endurance athletes — but we
  // keep "biggest deviation" within each band on top).
  const issues = [];
  if (latestLab) {
    for (const [k, st] of Object.entries(latestLab.status || {})) {
      if (st !== "low" && st !== "high") continue;
      const p = paramByKey[k];
      if (!p) continue;
      const v = latestLab.params?.[k];
      if (v == null) continue;
      const lo = p.range_low;
      const hi = p.range_high;
      const deviation =
        st === "high" && hi != null ? v - hi
          : st === "low" && lo != null ? lo - v
          : null;
      // Percentage deviation gives a unit-agnostic feel for severity.
      const refMid = lo != null && hi != null ? (lo + hi) / 2 : null;
      const deviationPct =
        deviation != null && refMid ? Math.round((deviation / refMid) * 100) : null;
      issues.push({
        key: k,
        label: p.label,
        group: p.group,
        unit: p.unit,
        value: v,
        status: st,
        rangeLow: lo,
        rangeHigh: hi,
        deviation,
        deviationPct,
      });
    }
  }

  // Within each status, biggest deviation% first — most clinically interesting.
  issues.sort((a, b) => {
    if (a.status !== b.status) return a.status === "high" ? -1 : 1;
    const pa = a.deviationPct ?? 0;
    const pb = b.deviationPct ?? 0;
    return pb - pa;
  });

  const lowCount = issues.filter((i) => i.status === "low").length;
  const highCount = issues.filter((i) => i.status === "high").length;

  return (
    <div className="report-doc">
      {/* HEADER */}
      <header className="border-b-2 border-rose-400/60 pb-5 mb-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="/branding/favicon-192.png"
              alt="YCH"
              className="w-14 h-14 rounded-lg bg-white p-1 object-contain border border-rose-500/40 print:bg-white"
            />
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-rose-300/80">
                Yacht Club of Hyderabad — Lab Deviations
              </div>
              <div className="font-display font-extrabold text-3xl mt-0.5 text-slate-100">
                {detail.name}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                <span
                  className="px-2 py-0.5 rounded-full"
                  style={{
                    background: `${GROUP_COLORS[detail.group] || "#94A3B8"}22`,
                    color: GROUP_COLORS[detail.group] || "#94A3B8",
                    border: `1px solid ${GROUP_COLORS[detail.group] || "#94A3B8"}66`,
                  }}
                >
                  {detail.group}
                </span>
                {detail.subgroup && (
                  <span className="px-2 py-0.5 rounded-full border border-cyan-500/40 text-cyan-200 bg-cyan-500/10 font-mono-data">
                    {detail.subgroup}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-400 font-mono-data">
            <div>Report generated</div>
            <div className="text-slate-200 font-display text-base">{fmtDate(today)}</div>
            <div className="mt-2">Lab date</div>
            <div className="text-rose-300 font-display text-base" data-testid="issues-report-lab-date">
              {latestLab ? fmtDate(latestLab.date) : "—"}
            </div>
            {latestLab?.hospital && (
              <div className="text-[10px] text-slate-500 mt-0.5">{latestLab.hospital}</div>
            )}
          </div>
        </div>
      </header>

      {/* Summary band */}
      <section className="mb-6 grid grid-cols-3 gap-3">
        <StatCard
          label="Out-of-range"
          value={issues.length}
          accent={issues.length === 0 ? "#10B981" : "#EF4444"}
        />
        <StatCard label="Low" value={lowCount} accent="#A78BFA" />
        <StatCard label="High" value={highCount} accent="#F87171" />
      </section>

      {/* Empty state — all values normal */}
      {issues.length === 0 ? (
        <section className="border border-emerald-500/40 bg-emerald-500/5 rounded-lg p-6 text-center" data-testid="issues-report-clean">
          <div className="text-emerald-300 font-display text-lg">All parameters within range</div>
          <div className="text-slate-400 text-sm mt-1">
            {latestLab
              ? `No deviations recorded in the lab test from ${fmtDate(latestLab.date)}.`
              : "No lab tests on file yet."}
          </div>
        </section>
      ) : (
        <>
          {/* Explain-with-Claude button — hidden from the printed PDF via
              .print:hidden / data-noprint attribute so the doctor's copy
              never has the coach summary in it. */}
          {onExplain && (
            <section className="mb-3 flex justify-end" data-noprint-toolbar>
              <button
                onClick={() =>
                  onExplain(latestLab, issues.map((i) => ({
                    key: i.key,
                    label: i.label,
                    value: i.value,
                    unit: i.unit,
                    status: i.status,
                    range_low: i.rangeLow,
                    range_high: i.rangeHigh,
                  })))
                }
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs uppercase tracking-wider border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                data-testid="explain-issues-btn"
                title="Generate a plain-English coach summary using Claude Sonnet 4.5"
              >
                <Sparkles size={12} /> Explain with Claude
              </button>
            </section>
          )}

          <section className="space-y-2" data-testid="issues-report-list">
            {issues.map((iss) => (
              <DeviationRow key={iss.key} iss={iss} />
            ))}
          </section>
        </>
      )}

      <footer className="mt-10 pt-4 border-t border-slate-800 text-[10px] text-slate-500 flex justify-between">
        <span>Yacht Club of Hyderabad — confidential medical record</span>
        <span className="font-mono-data">Reference ranges per Tata 1mg lab; status flags are advisory.</span>
      </footer>
    </div>
  );
}

function DeviationRow({ iss }) {
  const isHigh = iss.status === "high";
  const accent = isHigh ? "#EF4444" : "#A78BFA";
  const label = isHigh ? "HIGH" : "LOW";
  const refStr =
    iss.rangeLow != null && iss.rangeHigh != null
      ? `${fmtNum(iss.rangeLow, 2)} – ${fmtNum(iss.rangeHigh, 2)}`
      : iss.rangeLow != null
      ? `≥ ${fmtNum(iss.rangeLow, 2)}`
      : iss.rangeHigh != null
      ? `≤ ${fmtNum(iss.rangeHigh, 2)}`
      : "—";
  const devStr =
    iss.deviation != null
      ? `${isHigh ? "+" : "−"}${fmtNum(Math.abs(iss.deviation), 2)} ${iss.unit || ""}`.trim()
      : null;

  return (
    <div
      className="flex flex-wrap items-center gap-3 border rounded-lg px-4 py-2.5 break-inside-avoid"
      style={{ borderColor: `${accent}66`, background: `${accent}0F` }}
      data-testid={`issue-row-${iss.key}`}
    >
      <div className="min-w-[180px]">
        <div className="text-slate-100 font-display text-base">{iss.label}</div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{iss.group}</div>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono-data font-bold text-xl" style={{ color: accent }}>
          {fmtNum(iss.value, 2)}
        </span>
        <span className="text-xs text-slate-500">{iss.unit || ""}</span>
      </div>
      <div
        className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-mono-data"
        style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}66` }}
      >
        {label}
      </div>
      <div className="ml-auto text-right">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Reference</div>
        <div className="font-mono-data text-sm text-slate-300">{refStr}</div>
      </div>
      {devStr && (
        <div className="text-right min-w-[100px]">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Deviation</div>
          <div className="font-mono-data text-sm" style={{ color: accent }}>
            {devStr}
            {iss.deviationPct != null && (
              <span className="text-[10px] text-slate-500 ml-1">
                ({isHigh ? "+" : "−"}{Math.abs(iss.deviationPct)}%)
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function parameters_keys_in(record, paramByKey) {
  // Preserve canonical order from parameters list
  const out = [];
  for (const p of Object.values(paramByKey)) out.push(p);
  return out;
}

function StatCard({ label, value, accent = "#00E5FF", small }) {
  return (
    <div className="border border-slate-700/70 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className={`font-display font-extrabold ${small ? "text-base" : "text-2xl"} mt-0.5`} style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="font-display text-base text-slate-100 mb-2 pb-1 border-b border-slate-800">
      {children}
    </h2>
  );
}

function ValuePill({ label, value, unit, status }) {
  const cls =
    status === "low" ? "border-violet-400/60 bg-violet-500/10 text-violet-200" :
    status === "high" ? "border-rose-400/60 bg-rose-500/10 text-rose-200" :
    "border-emerald-400/50 bg-emerald-500/10 text-emerald-200";
  return (
    <div className={`border rounded px-2 py-1.5 ${cls}`}>
      <div className="text-[10px] opacity-80 truncate">{label}</div>
      <div className="flex items-baseline justify-between mt-0.5">
        <span className="font-mono-data text-sm font-bold">{fmtNum(value, 2)}</span>
        <span className="text-[9px] opacity-70">{unit}</span>
      </div>
      {STATUS_LABEL[status] && (
        <div className="text-[8px] font-mono-data opacity-90 mt-0.5 tracking-wider">{STATUS_LABEL[status]}</div>
      )}
    </div>
  );
}

function cellColor(st) {
  if (st === "low") return "text-violet-300";
  if (st === "high") return "text-rose-300";
  if (st === "ok") return "text-emerald-300";
  return "text-slate-300";
}

function countIssues(rec) {
  if (!rec) return 0;
  return Object.values(rec.status || {}).filter((s) => s === "low" || s === "high").length;
}

function yearsBetween(a, b) {
  if (!a || !b) return 0;
  const d = (new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24 * 365.25);
  return d.toFixed(1);
}

function stripDrPrefix(s) {
  return s.replace(/^dr\.?\s+/i, "").trim();
}

// --- Baseline rendering for the print report ---

function formatBaselineValue(field, v) {
  if (v === undefined || v === null || v === "") return null;
  if (Array.isArray(v)) {
    if (v.length === 0) return null;
    return v.join(", ");
  }
  if (typeof v === "boolean") return v ? "Yes" : null;
  if (field?.type === "date" && typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    return fmtDate(v);
  }
  if (field?.unit) return `${v} ${field.unit}`;
  return String(v);
}

function BaselineSection({ baseline }) {
  return (
    <section className="mb-8 page-block" data-testid="report-baseline">
      <h2 className="font-display text-base text-slate-100 mb-3 pb-1 border-b border-slate-800 flex items-center gap-2">
        <ClipboardCheck size={14} className="text-cyan-300" />
        Baseline Assessment
      </h2>
      {BASELINE_SCHEMA.map((section) => {
        const filled = section.fields
          .map((f) => ({ f, v: formatBaselineValue(f, baseline[f.key]) }))
          .filter((x) => x.v !== null);
        if (filled.length === 0) return null;
        return (
          <div key={section.id} className="mb-4 break-inside-avoid">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300/70 mb-1">{section.section}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-[12px]">
              {filled.map(({ f, v }) => (
                <div key={f.key} className="flex flex-col border border-slate-800 rounded px-2 py-1">
                  <span className="text-[10px] text-slate-500">{f.label}</span>
                  <span className="text-slate-200 break-words">{v}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

// ----------------------------------------------------------------------------
// IncidentDetailsBlock — renders the YCH incident form fields (location,
// vitals, actions, person informed, hospital referral) underneath the
// per-injury card in the printable PDF. Only renders sections that actually
// have data so quick-logged injuries stay compact.
// ----------------------------------------------------------------------------
const ACTION_LABEL = {
  first_aid: "First aid given",
  ice: "Ice applied",
  medication: "Medication given",
  hospital_referral: "Referred to hospital/clinic",
};
const INFORMED_ROLE_LABEL = {
  parent: "Parent/Guardian",
  coach: "Coach",
  admin: "Admin",
  doctor: "Doctor",
  other: "Other",
};

function IncidentDetailsBlock({ inj }) {
  const v = inj.vitals || {};
  const inf = inj.informed || {};
  const r = inj.referral || {};

  const headerBits = [];
  if (inj.time_of_incident) headerBits.push(`${inj.time_of_incident}`);
  if (inj.location) headerBits.push(inj.location);
  if (inj.reported_by) headerBits.push(`Reported: ${inj.reported_by}`);
  if (inj.report_received_by) headerBits.push(`Received: ${inj.report_received_by}`);

  const vitalsList = [
    v.bp && `BP ${v.bp}`,
    v.temp && `Temp ${v.temp}`,
    v.pr && `PR ${v.pr}`,
    v.spo2 && `SpO₂ ${v.spo2}`,
    v.sugar && `Sugar ${v.sugar}`,
  ].filter(Boolean);

  const actionsList = (inj.actions || []).map((a) => ACTION_LABEL[a] || a);
  const hasInformed = inf.role || inf.name || inf.at_time;
  const hasReferral = r.hospital || r.time_left || r.time_arrived || r.doctor
    || r.escort_name || r.contact_no || r.parent_arrival || r.handover_time;

  // If literally nothing was filled, render nothing.
  const anything = headerBits.length || vitalsList.length || inj.symptoms
    || actionsList.length || hasInformed || hasReferral;
  if (!anything) return null;

  return (
    <div className="mt-2 pl-2 border-l border-dashed border-slate-700/60 space-y-1.5 text-[11px]">
      {headerBits.length > 0 && (
        <div className="text-slate-400">{headerBits.join(" · ")}</div>
      )}
      {vitalsList.length > 0 && (
        <div>
          <span className="text-cyan-300 font-semibold uppercase text-[9px] mr-1.5">Vitals</span>
          <span className="font-mono-data text-slate-300">{vitalsList.join(" · ")}</span>
        </div>
      )}
      {inj.symptoms && (
        <div>
          <span className="text-cyan-300 font-semibold uppercase text-[9px] mr-1.5">Symptoms</span>
          <span className="text-slate-300 whitespace-pre-wrap">{inj.symptoms}</span>
        </div>
      )}
      {actionsList.length > 0 && (
        <div>
          <span className="text-cyan-300 font-semibold uppercase text-[9px] mr-1.5">Action taken</span>
          <span className="text-slate-300">{actionsList.join(" · ")}</span>
        </div>
      )}
      {hasInformed && (
        <div>
          <span className="text-cyan-300 font-semibold uppercase text-[9px] mr-1.5">Informed</span>
          <span className="text-slate-300">
            {[inf.role && (INFORMED_ROLE_LABEL[inf.role] || inf.role),
              inf.name, inf.at_time && `at ${inf.at_time}`]
              .filter(Boolean).join(" — ")}
          </span>
        </div>
      )}
      {hasReferral && (
        <div className="border border-slate-800 rounded px-2 py-1">
          <div className="text-amber-300 font-semibold uppercase text-[9px] mb-0.5">Hospital referral</div>
          <div className="text-slate-300 space-y-0.5">
            {r.hospital && <div>{r.hospital}{r.doctor ? ` — ${r.doctor}` : ""}</div>}
            {(r.time_left || r.time_arrived) && (
              <div className="font-mono-data text-[10px] text-slate-400">
                {r.time_left && `Left ${r.time_left}`}
                {r.time_left && r.time_arrived && " · "}
                {r.time_arrived && `Arrived ${r.time_arrived}`}
              </div>
            )}
            {(r.escort_name || r.contact_no) && (
              <div className="text-[10px] text-slate-400">
                Escort: {r.escort_name}{r.contact_no ? ` (${r.contact_no})` : ""}
              </div>
            )}
            {(r.parent_arrival || r.handover_time) && (
              <div className="font-mono-data text-[10px] text-slate-400">
                {r.parent_arrival && `Parent arrived ${r.parent_arrival}`}
                {r.parent_arrival && r.handover_time && " · "}
                {r.handover_time && `Handover ${r.handover_time}`}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
